import React, { useRef, useState } from "react";
import {
  Avatar,
  Box,
  ButtonBase,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from "@mui/material";
import {
  ChevronRight,
  ExitToApp,
  KeyboardArrowDown,
  LockOutlined,
  Person,
  ShieldOutlined,
} from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import vyontaLogoInk from '../../assets/Vyonta-logo-ink.png';

const colors = {
  ink: "#06185f",
  muted: "#4c5d86",
  blue: "#0059f6",
  line: "#d9e3f4",
};

const shakeKeyframes = {
  "@keyframes shake": {
    "0%, 100%": { transform: "translateX(0)" },
    "10%, 30%, 50%, 70%, 90%": { transform: "translateX(-5px)" },
    "20%, 40%, 60%, 80%": { transform: "translateX(5px)" },
  },
};

const getInitials = (name) => {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

export default function ProfileSelectionUI({
  empresaNombre,
  team,
  loading,
  onBack,
  onProfileSelect,
  pin,
  pinError,
  onPinChange,
  onLogout,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isPinFocused, setIsPinFocused] = useState(false);
  const inputRef = useRef(null);
  const openMenu = Boolean(anchorEl);

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLogoutClick = () => {
    handleMenuClose();
    onLogout?.();
  };

  const renderPinText = () => {
    if (pinError) return "ERROR";

    const filled = "X ".repeat(pin.length);
    const empty = "- ".repeat(4 - pin.length);
    return `${filled}${empty}`.trim();
  };

  return (
    <Box
      sx={{
        height: "100dvh",
        width: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        color: colors.ink,
      }}
    >
      <Box
        component="header"
        sx={{
          height: 60,
          px: { xs: 2, sm: 4 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "sticky",
          top: 0,
          zIndex: 20,
          backgroundColor: "rgba(255,255,255,0.94)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderRadius: "0 0 10px 10px",
          boxShadow: "0 8px 22px rgba(15, 42, 89, 0.12)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
          <Box
            sx={{
              width: 96,
              height: 38,
              position: "relative",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={vyontaLogoInk}
              alt="Vyonta"
              sx={{
                position: "absolute",
                width: 164,
                height: "auto",
                left: -33,
                top: -29,
                display: "block",
              }}
            />
          </Box>

          <ButtonBase
            onClick={onBack}
            sx={{
              height: 32,
              px: 1.5,
              gap: 1,
              borderRadius: 999,
              color: colors.ink,
              backgroundColor: "#f2f7ff",
              border: "1px solid #dce8ff",
              boxShadow: "0 6px 16px rgba(0, 89, 246, 0.08)",
              transition: "background-color 160ms ease, transform 160ms ease",
              "&:hover": {
                backgroundColor: "#eaf3ff",
                transform: "translateY(-1px)",
              },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18, color: colors.blue }} />
            <Typography
              sx={{
                display: { xs: "none", sm: "block" },
                fontSize: 12,
                fontWeight: 800,
              }}
            >
              Volver al menú
            </Typography>
          </ButtonBase>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.8, sm: 1.4 } }}>
          <Box
            onClick={handleMenuClick}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.8, sm: 1.4 },
              cursor: "pointer",
              py: 0.5,
              pl: { xs: 0.5, sm: 1.2 },
              pr: 0.3,
              borderRadius: 999,
              transition: "background-color 160ms ease",
              "&:hover": { backgroundColor: "#f0f6ff" },
            }}
          >
            <Typography
              sx={{
                color: colors.ink,
                fontSize: { xs: 11.5, sm: 13 },
                fontWeight: 800,
                display: { xs: "none", sm: "block" },
                maxWidth: 180,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {empresaNombre || "Empresa"}
            </Typography>

            <Avatar
              sx={{
                width: { xs: 34, sm: 38 },
                height: { xs: 34, sm: 38 },
                color: colors.blue,
                backgroundColor: "#f5f9ff",
                border: "1px solid #bed3ff",
                boxShadow: "0 4px 12px rgba(0, 89, 246, 0.10)",
              }}
            >
              <Person sx={{ fontSize: 20 }} />
            </Avatar>

            <KeyboardArrowDown sx={{ color: colors.blue, fontSize: 20 }} />
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={openMenu}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
            PaperProps={{
              elevation: 0,
              sx: {
                border: "1px solid #dce5f2",
                borderRadius: "10px",
                boxShadow: "0 16px 32px rgba(19, 39, 84, 0.14)",
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <MenuItem onClick={handleLogoutClick}>
              <ListItemIcon>
                <ExitToApp fontSize="small" />
              </ListItemIcon>
              <ListItemText>Cerrar Sesión</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      <Box
        component="main"
        sx={{
          flex: 1,
          height: "calc(100dvh - 60px)",
          overflowY: "auto",
          px: { xs: 2, sm: 4 },
          py: { xs: 3, sm: 5 },
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "80%",
            mx: "auto",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 420px" },
            columnGap: { xs: 3, md: 9 },
            rowGap: 4,
            alignItems: "start",
          }}
        >
          <Box sx={{ pt: 2}}>
            <Typography
              component="h1"
              sx={{
                color: colors.ink,
                fontSize: 38,
                fontWeight: 900,
                letterSpacing: "-0.9px",
              }}
            >
              Panel de Acceso
            </Typography>

            <Typography sx={{ mt: 1, color: colors.muted, fontSize: 14, fontWeight: 500 }}>
              Elegí un vendedor o ingresá como supervisor.
            </Typography>
          </Box>

          <Paper
            elevation={0}
            onClick={() => inputRef.current?.focus()}
            sx={{
              ...shakeKeyframes,
              width: "100%",
              p: { xs: 2, sm: 3 },
              borderRadius: "10px",
              cursor: "text",
              backgroundColor: "rgba(255,255,255,0.94)",
              border: pinError
                ? "1px solid #ff4d4f"
                : isPinFocused
                ? `1px solid ${colors.blue}`
                : "1px solid #dce5f2",
              boxShadow: pinError
                ? "0 16px 34px rgba(255, 77, 79, 0.13)"
                : "0 18px 38px rgba(19, 39, 84, 0.12)",
              animation: pinError ? "shake 0.4s cubic-bezier(.36,.07,.19,.97) both" : "none",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "10px",
                  display: "grid",
                  placeItems: "center",
                  color: colors.blue,
                  backgroundColor: "#edf4ff",
                  flexShrink: 0,
                }}
              >
                <ShieldOutlined sx={{ fontSize: 24 }} />
              </Box>

              <Box>
                <Typography sx={{ color: colors.ink, fontSize: 24, fontWeight: 900 }}>
                  Supervisor
                </Typography>
                <Typography sx={{ mt: 1, color: colors.ink, fontSize: 14, fontWeight: 800 }}>
                  Ingreso con PIN
                </Typography>
                <Typography sx={{ mt: 0.2, color: colors.muted, fontSize: 12 }}>
                  Solo personal autorizado
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                mt: 2,
                minHeight: 48,
                px: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                position: "relative",
                overflow: "hidden",
                borderRadius: "10px",
                color: "#fff",
                background: pinError
                  ? "linear-gradient(135deg, #ffeded 0%, #ffe1e1 100%)"
                  : `${colors.blue}`,
                border: pinError ? "1px solid #ff4d4f" : "1px solid rgba(255,255,255,0.14)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LockOutlined sx={{ fontSize: 16, color: pinError ? "#ff4d4f" : "#fff" }} />
                <Typography sx={{ fontWeight: 900, fontSize: 14, letterSpacing: 0.8, color: pinError ? "#ff4d4f" : "#fff" }}>
                  PIN
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontFamily: "monospace",
                  fontSize: 16,
                  fontWeight: 900,
                  letterSpacing: 6,
                  color: pinError ? "#ff4d4f" : "#fff",
                  whiteSpace: "nowrap",
                }}
              >
                {renderPinText()}
              </Typography>

              <input
                ref={inputRef}
                type="tel"
                value={pin}
                onChange={(e) => onPinChange(e.target.value)}
                onFocus={() => setIsPinFocused(true)}
                onBlur={() => setIsPinFocused(false)}
                maxLength={4}
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  opacity: 0,
                  cursor: "text",
                  border: "none",
                  outline: "none",
                }}
                autoComplete="off"
              />
            </Box>
          </Paper>

          <Box sx={{ gridColumn: { xs: "1", md: "1 / -1" }, mt: 1 }}>
            <Typography
              component="h2"
              sx={{
                color: colors.ink,
                fontSize: 28,
                fontWeight: 900,
                letterSpacing: "-0.5px",
                mb: 2,
              }}
            >
              Equipo de Ventas
            </Typography>

            {loading ? (
              <Typography sx={{ color: colors.muted, fontSize: 14, fontWeight: 600 }}>
                Cargando equipo...
              </Typography>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(128px, 1fr))",
                    sm: "repeat(auto-fill, minmax(182px, 182px))",
                  },
                  gap: { xs: 1.8, sm: 2 },
                  alignItems: "stretch",
                }}
              >
                {team.map((user) => (
                  <ButtonBase
                    key={user.id}
                    onClick={() => onProfileSelect(user)}
                    sx={{
                      width: "100%",
                      minHeight: 120,
                      py: 2,
                      px: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      backgroundColor: "rgba(255,255,255,0.94)",
                      border: "1px solid #dce5f2",
                      borderRadius: "10px",
                      boxShadow: "0 14px 28px rgba(19, 39, 84, 0.10)",
                      transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        borderColor: "rgba(0,89,246,0.36)",
                        boxShadow: "0 18px 34px rgba(19, 39, 84, 0.14)",
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 60,
                        height: 60,
                        mb: 1.5,
                        color: "#fff",
                        backgroundColor: colors.blue,
                        border: "4px solid #fff",
                        boxShadow: "0 8px 18px rgba(19, 39, 84, 0.22)",
                        fontSize: 22,
                        fontWeight: 700,
                      }}
                    >
                      {getInitials(user.nombre)}
                    </Avatar>

                    <Typography
                      sx={{
                        width: "100%",
                        color: colors.ink,
                        fontSize: 14,
                        fontWeight: 900,
                        lineHeight: 1.2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {user.nombre}
                    </Typography>

                    <Typography sx={{ mt: 0.15, color: colors.muted, fontSize: 11, fontWeight: 500 }}>
                      Vendedor
                    </Typography>

                    <Box
                      sx={{
                        mt: 1.1,
                        width: 24,
                        height: 24,
                        display: "grid",
                        placeItems: "center",
                        borderRadius: "50%",
                        color: colors.blue,
                        backgroundColor: "#edf4ff",
                      }}
                    >
                      <ChevronRight sx={{ fontSize: 18 }} />
                    </Box>
                  </ButtonBase>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
